import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Logo } from '../common';

import { useAuth } from '../../contexts';
import { useCompany } from '../../contexts/CompanyContext';

const Header = ({ onMenuClick, className = '' }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { companyData } = useCompany();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const profileMenuItems = [
    {
      name: 'Your Profile',
      href: '/profile',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
  ];

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      setIsProfileOpen(false);
      navigate('/login', { replace: true });
    }
  };

  return (
    <header
      className={`bg-white shadow-sm border-b border-[#fff7e0] ${className}`}
    >
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Left side - Mobile menu button */}
        <div className="flex items-center">
          <button
            type="button"
            className="lg:hidden p-2 rounded-md text-gray-500 hover:text-[#601e00] hover:bg-[#fff7e0] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#fdbd0a]"
            onClick={onMenuClick}
          >
            <span className="sr-only">Open sidebar</span>
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>

          {/* Logo and Page title */}
          <div className="ml-4 lg:ml-0 flex items-center">
            <Logo 
              src={companyData.logo || "/images/logo-icon.png"} 
              size="lg" 
              className="mr-3" 
              alt={`${companyData.name} Logo`}
            />
            <div className="flex flex-col">
              <h1 className="text-lg font-semibold text-[#601e00]">
                {companyData.name}
              </h1>
              {user?.role && (
                <span className="hidden lg:block text-[10px] font-bold uppercase tracking-wider text-amber-600 -mt-1">
                  {user.role}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Profile & Role Badge */}
        <div className="flex items-center gap-4">
          {/* Desktop Role Badge */}
          <div className="hidden sm:flex items-center px-3 py-1 rounded-full bg-amber-50 border border-amber-200">
            <div className="w-2 h-2 rounded-full bg-amber-500 mr-2 animate-pulse" />
            <span className="text-xs font-semibold text-amber-800 uppercase tracking-tight">
              {user?.role || 'User'}
            </span>
          </div>

          {/* Profile dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              type="button"
              className="flex items-center max-w-xs text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#fdbd0a] transition-transform hover:scale-105"
              onClick={() => setIsProfileOpen(!isProfileOpen)}
            >
              <span className="sr-only">Open user menu</span>
              <div className="w-8 h-8 bg-[#fff7e0] rounded-full flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-[#601e00]"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </button>

            {/* Dropdown menu */}
            {isProfileOpen && (
              <div className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-xl bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999] border border-gray-200">
                <div className="py-1">
                  {/* User info */}
                  <div className="px-4 py-2 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900">
                      {user?.name || 'User'}
                    </p>
                    <p className="text-sm text-gray-500">{user?.email || '—'}</p>
                  </div>

                  {/* Menu items */}
                  {profileMenuItems.map(item => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <span className="mr-3 text-gray-400">{item.icon}</span>
                      {item.name}
                    </Link>
                  ))}

                  {/* Sign out */}
                  <div className="border-t border-gray-100">
                    <button
                      type="button"
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      onClick={handleSignOut}
                    >
                      <span className="mr-3 text-gray-400">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                      </span>
                      Sign out
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  onMenuClick: PropTypes.func.isRequired,
  className: PropTypes.string,
};

export default Header;
